import { useState, useEffect } from "react";
import { 
  Search, Package, Truck, CheckCircle2, Clock, AlertTriangle,
  ChevronDown, Eye, Building2, Car, Euro, Send, XCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: "In Attesa", color: "bg-yellow-500/20 text-yellow-400", icon: Clock },
  processing: { label: "In Lavorazione", color: "bg-blue-500/20 text-blue-400", icon: Package },
  ready_for_pickup: { label: "Pronto per il Ritiro", color: "bg-cyan-500/20 text-cyan-400", icon: CheckCircle2 },
  partial_shipped: { label: "Spedito Parziale", color: "bg-orange-500/20 text-orange-400", icon: Truck },
  shipped: { label: "Spedito", color: "bg-purple-500/20 text-purple-400", icon: Truck },
  delivered: { label: "Consegnato", color: "bg-green-500/20 text-green-400", icon: CheckCircle2 },
  cancelled: { label: "Annullato", color: "bg-red-500/20 text-red-400", icon: XCircle },
};

export default function RicambistaOrdini({ user }) {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateData, setUpdateData] = useState({
    status: "",
    shipping_status: "",
    delivery_notes: "",
    tracking_number: ""
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, filterStatus]);

  const fetchOrders = async () => {
    try {
      const res = await authFetch(`${API}/ricambista/orders/manage`, {
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Errore nel caricamento ordini");
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o => 
        o.officina_info?.company_name?.toLowerCase().includes(term) ||
        o.order_number?.toString().includes(term) ||
        o.vehicle_info?.plate?.toLowerCase().includes(term)
      );
    }
    
    if (filterStatus !== "all") {
      filtered = filtered.filter(o => o.status === filterStatus);
    }
    
    setFilteredOrders(filtered);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowDetailDialog(true);
  };

  const handleOpenUpdateDialog = (order) => {
    setSelectedOrder(order);
    setUpdateData({
      status: order.status,
      shipping_status: order.shipping_status || "",
      delivery_notes: order.delivery_notes || "",
      tracking_number: order.tracking_number || ""
    });
    setShowUpdateDialog(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder || !updateData.status) return;
    
    try {
      const res = await authFetch(`${API}/ricambista/orders/manage/${selectedOrder.ricambista_order_id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData)
      });
      
      if (res.ok) {
        toast.success("Stato ordine aggiornato");
        setShowUpdateDialog(false);
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const calculateTotal = (items) => {
    return items?.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0) || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Ordini da Gestire</h1>
          <p className="text-zinc-400">Gestisci gli ordini ricevuti dalle officine</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const count = orders.filter(o => o.status === key).length;
          const Icon = config.icon;
          return (
            <div 
              key={key}
              className={`p-4 rounded-lg bg-zinc-900/50 border border-zinc-800 cursor-pointer hover:border-zinc-700 ${filterStatus === key ? 'border-primary' : ''}`}
              onClick={() => setFilterStatus(filterStatus === key ? "all" : key)}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-zinc-400" />
                <span className="text-2xl font-bold text-white">{count}</span>
              </div>
              <p className="text-sm text-zinc-400 mt-1">{config.label}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Cerca per officina, numero ordine, targa..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800">
            <Package className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400">Nessun ordine trovato</p>
          </div>
        ) : (
          filteredOrders.map((order) => (
            <div 
              key={order.ricambista_order_id}
              className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-primary font-mono text-lg">#{order.order_number}</span>
                    {getStatusBadge(order.status)}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {order.officina_info?.company_name || "N/A"}
                    </span>
                    {order.vehicle_info?.plate && (
                      <span className="flex items-center gap-1">
                        <Car className="w-4 h-4" />
                        {order.vehicle_info.plate}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Package className="w-4 h-4" />
                      {order.items?.length || 0} articoli
                    </span>
                    <span className="flex items-center gap-1">
                      <Euro className="w-4 h-4" />
                      {calculateTotal(order.items).toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleViewOrder(order)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Dettagli
                  </Button>
                  <Button 
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                    onClick={() => handleOpenUpdateDialog(order)}
                  >
                    <Send className="w-4 h-4 mr-1" />
                    Aggiorna Stato
                  </Button>
                </div>
              </div>
              
              {/* Items preview */}
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <p className="text-xs text-zinc-500 mb-2">Articoli ordinati:</p>
                <div className="flex flex-wrap gap-2">
                  {order.items?.slice(0, 3).map((item, idx) => (
                    <span key={idx} className="text-xs bg-zinc-800 px-2 py-1 rounded">
                      {item.item_code || item.description?.substring(0, 20)}
                    </span>
                  ))}
                  {order.items?.length > 3 && (
                    <span className="text-xs text-zinc-500">+{order.items.length - 3} altri</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Ordine #{selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedOrder.status)}
                <span className="text-sm text-zinc-400">
                  {new Date(selectedOrder.created_at).toLocaleDateString('it-IT')}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800/50 rounded-lg">
                <div>
                  <p className="text-xs text-zinc-500">Officina</p>
                  <p className="text-white">{selectedOrder.officina_info?.company_name}</p>
                  <p className="text-sm text-zinc-400">{selectedOrder.officina_info?.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Veicolo</p>
                  <p className="text-white">{selectedOrder.vehicle_info?.plate}</p>
                  <p className="text-sm text-zinc-400">
                    {selectedOrder.vehicle_info?.brand} {selectedOrder.vehicle_info?.model}
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-white mb-2">Articoli</p>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-zinc-800/50 rounded">
                      <div>
                        <p className="text-white">{item.description || item.item_code}</p>
                        <p className="text-xs text-zinc-400">Cod: {item.item_code} - {item.brand}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white">x{item.quantity}</p>
                        <p className="text-primary">€{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-between items-center pt-4 border-t border-zinc-800">
                <span className="text-zinc-400">Totale</span>
                <span className="text-2xl font-bold text-primary">
                  €{calculateTotal(selectedOrder.items).toFixed(2)}
                </span>
              </div>
              
              {selectedOrder.tracking_number && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                  <p className="text-xs text-blue-400">Tracking</p>
                  <p className="text-white">{selectedOrder.tracking_number}</p>
                </div>
              )}
              
              {selectedOrder.delivery_notes && (
                <div className="p-3 bg-zinc-800/50 rounded">
                  <p className="text-xs text-zinc-500">Note consegna</p>
                  <p className="text-white">{selectedOrder.delivery_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiorna Stato Ordine #{selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Stato Ordine</label>
              <Select 
                value={updateData.status} 
                onValueChange={(v) => setUpdateData(prev => ({...prev, status: v}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">In Attesa</SelectItem>
                  <SelectItem value="processing">In Lavorazione</SelectItem>
                  <SelectItem value="ready_for_pickup">Pronto per il Ritiro</SelectItem>
                  <SelectItem value="partial_shipped">Spedito Parzialmente</SelectItem>
                  <SelectItem value="shipped">Spedito Completo</SelectItem>
                  <SelectItem value="delivered">Consegnato</SelectItem>
                  <SelectItem value="cancelled">Annullato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(updateData.status === "shipped" || updateData.status === "partial_shipped") && (
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Numero Tracking</label>
                <Input
                  value={updateData.tracking_number}
                  onChange={(e) => setUpdateData(prev => ({...prev, tracking_number: e.target.value}))}
                  placeholder="Es: IT12345678"
                />
              </div>
            )}
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Note Consegna</label>
              <textarea
                className="w-full p-3 bg-zinc-800 border border-zinc-700 rounded-md text-white"
                rows={3}
                value={updateData.delivery_notes}
                onChange={(e) => setUpdateData(prev => ({...prev, delivery_notes: e.target.value}))}
                placeholder="Note per l'officina..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdateStatus} className="bg-primary hover:bg-primary/90">
              Aggiorna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
